import React, { useEffect, useMemo } from 'react';
import {
  buildVoterRegistrationUrl,
  VOTER_REG_MEDIUM_AUTO_POPUP
} from '../utils/voterRegistrationUtils';

// VoteAmerica's verify tool, embedded directly so submissions are attributed to
// our subscriber and DJs never leave the hub.
const VOTER_REGISTRATION_EMBED_URL =
  'https://www.voteamerica.org/embed/verify/?subscriber=djs-at-the-polls&edition=standard';

/**
 * VoterRegistrationPopup Component
 *
 * A popup that displays the VoteAmerica voter registration verification tool
 * using direct iframe embedding for users with "Hired" status.
 *
 * @param {Object} props - Component props
 * @param {function} props.onClose - Function to call when the popup is closed
 * @param {Object} [props.dj] - The DJ record, used to tag the submission with their rid
 * @param {Object} [props.election] - The election the DJ opened the tool from
 * @param {string} [props.medium] - Entry point the DJ came from
 * @returns {JSX.Element} - The rendered component
 */
const VoterRegistrationPopup = ({ onClose, dj, election, medium = VOTER_REG_MEDIUM_AUTO_POPUP }) => {
  // Tag the embed so the VoteAmerica export can be tied back to this DJ.
  const embedUrl = useMemo(
    () =>
      buildVoterRegistrationUrl(VOTER_REGISTRATION_EMBED_URL, {
        djRid: dj?.rid,
        electionRid: election?.rid,
        medium
      }),
    [dj?.rid, election?.rid, medium]
  );

  useEffect(() => {
    // Handle escape key to close popup
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Determine if we're on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center overflow-hidden">
      {/* Content container */}
      <div 
        className={`bg-white rounded-lg shadow-2xl overflow-hidden relative ${
          isMobile ? 'w-full h-full m-0 rounded-none' : 'w-11/12 max-w-4xl max-h-[90vh] m-4'
        }`}
      >
        {/* Header with close button */}
        <div className="text-white relative" style={{ backgroundColor: '#3966C3' }}>
          <div className="p-6">
            <h2 className="text-2xl font-bold text-center pr-8">Check Your Voter Registration</h2>
            <p className="text-center mt-2 text-blue-100">
            Election Day is Nov 3, 2026
            </p>
          </div>
          
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white/10 rounded-lg p-1 transition-colors duration-200"
            aria-label="Close voter registration popup"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-6 w-6"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              strokeWidth={2}
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
        </div>
        
        {/* Scrollable content area */}
        <div className="overflow-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          {/* VoteAmerica iframe */}
          <div className="p-6 md:p-8">
          {/* <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Why check your registration?</strong> Voter registration requirements can change, 
              and it's important to verify your status before heading to the polls. This tool helps 
              you quickly confirm you're ready to vote!
            </p>
          </div> */}
          
          {/* Direct iframe embed */}
          <div className="relative overflow-hidden rounded-lg" style={{ height: '600px' }}>
            <iframe
              src={embedUrl}
              width="100%"
              height="650"
              data-resizable="true"
              title="Check Your Voter Registration"
              className="w-full border-0 absolute top-0 left-0"
              style={{ minHeight: '650px', border: 0 }}
            />
          </div>
          
          {/* Additional information */}
          <div className="mt-6 text-center text-sm text-gray-600">
            <p>This verification tool is provided by VoteAmerica.</p>
            <p className="mt-2">
              Having trouble? Contact us at{' '}
              <a href="mailto:support@djsatthepolls.org" className="text-blue-600 hover:underline">
                support@djsatthepolls.org
              </a>
            </p>
          </div>

          {/* Skip button at bottom */}
          <div className="mt-8 pb-6 text-center">
            <button
              onClick={onClose}
              className="bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium py-2 px-6 rounded-md border border-gray-300 transition-colors duration-200"
            >
              Skip this step
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoterRegistrationPopup;